import { RadioGroup, RadioGroupItem, Checkbox } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { InputProtocol, OutputType, PresetType, EventJobConfig } from '@/api/media/live-event-jobs-types';
import { FormSectionCard } from './FormSectionCard';
import styles from './EventJobCreate.module.css';

const INPUT_PROTOCOLS: { value: InputProtocol; label: string }[] = [
  { value: 'rtmp', label: 'RTMP' },
  { value: 'srt', label: 'SRT' },
  { value: 'srt_unicast', label: 'SRT Unicast' },
];

const OUTPUT_OPTIONS: { value: OutputType; label: string }[] = [
  { value: 'rtmp', label: 'RTMP' },
  { value: 'http-flv', label: 'HTTP-FLV' },
  { value: 'hls', label: 'HLS' },
  { value: 'dash', label: 'DASH' },
];

interface StreamConfigSectionProps {
  inputProtocol: InputProtocol;
  outputs: OutputType[];
  presets: PresetType[];
  audioOnlyFormat: 'aac' | 'mp3';
  lowLatencyEnabled: boolean;
  config: EventJobConfig | undefined;
  errors: Partial<Record<'outputs' | 'presets', string>>;
  onChange: (field: string, value: unknown) => void;
}

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

// SRT protocol restricts outputs to HLS and DASH only
const SRT_ONLY = ['srt'];
const SRT_BLOCKED_OUTPUTS: OutputType[] = ['rtmp', 'http-flv'];

// Preset label overrides (transmux stays selectable per legacy)
const PRESET_LABEL_OVERRIDES: Record<string, string> = { transmux: 'Transmuxing only' };

/** Input protocol radio, output type checkboxes, preset checkboxes, audio-only options, low-latency. */
export function StreamConfigSection({
  inputProtocol, outputs, presets, audioOnlyFormat,
  lowLatencyEnabled, config, errors, onChange,
}: StreamConfigSectionProps) {
  const isSrt = SRT_ONLY.includes(inputProtocol);

  // Derived outputs: strip SRT-incompatible outputs when in SRT mode
  const handleOutputToggle = (value: OutputType) => {
    if (isSrt && SRT_BLOCKED_OUTPUTS.includes(value)) return;
    onChange('outputs', toggle(outputs, value));
  };

  // Build display presets: exclude __snapshot__ (internal), keep transmux (relabeled),
  // always append audio_only at end (legacy StreamConfigSection:129-138)
  const configPresets = config?.presets ?? [];
  const basePresets = configPresets
    .filter((p) => p.value !== '__snapshot__')
    .map((p) => ({ value: p.value as PresetType, label: PRESET_LABEL_OVERRIDES[p.value] ?? (p.label || p.value) }));
  // Append audio_only if not already present (legacy always appends)
  const displayPresets = basePresets.some((p) => p.value === 'audio_only')
    ? basePresets
    : [...basePresets, { value: 'audio_only' as PresetType, label: 'Audio Only' }];

  // Fallback when config is not yet loaded
  const presetsToShow = displayPresets.length > 1 ? displayPresets : [
    { value: '1080p' as PresetType, label: '1080p' },
    { value: '720p' as PresetType, label: '720p' },
    { value: '480p' as PresetType, label: '480p' },
    { value: '360p' as PresetType, label: '360p' },
    { value: 'transmux' as PresetType, label: 'Transmuxing only' },
    { value: 'audio_only' as PresetType, label: 'Audio Only' },
  ];

  return (
    <FormSectionCard title="Stream Configuration" description="Input protocol, outputs, and transcoding presets.">
      {/* Input protocol — single-select radio group */}
      <fieldset className={styles.fieldsetReset} aria-required="true">
        <legend className={styles.fieldLegend}>Input Protocol <span aria-hidden="true">*</span></legend>
        <RadioGroup
          value={inputProtocol}
          onValueChange={(v) => onChange('input_protocol', v as InputProtocol)}
          className={styles.radioGroup}
        >
          {INPUT_PROTOCOLS.map(({ value, label }) => (
            <label key={value} className={styles.radioItem}>
              <RadioGroupItem value={value} id={`input-protocol-${value}`} />
              <span>{label}</span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      {/* Output types — multi-select checkboxes; SRT-blocked items are disabled */}
      <Field label="Output Types" required error={errors.outputs}>
        {() => (
          <>
            <div className={styles.checkGroup}>
              {OUTPUT_OPTIONS.map(({ value, label }) => {
                const blocked = isSrt && SRT_BLOCKED_OUTPUTS.includes(value);
                return (
                  <label
                    key={value}
                    className={`${styles.checkItem} ${blocked ? styles.checkItemDisabled ?? '' : ''}`}
                    title={blocked ? 'Not available for SRT input' : undefined}
                  >
                    <Checkbox
                      checked={outputs.includes(value)}
                      disabled={blocked}
                      aria-disabled={blocked}
                      onCheckedChange={() => handleOutputToggle(value)}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
            {isSrt && (
              <p className={styles.hint}>SRT input supports HLS and DASH outputs only.</p>
            )}
          </>
        )}
      </Field>

      {/* Presets — multi-select checkboxes */}
      <Field label="Presets" required error={errors.presets}>
        {() => (
          <div className={styles.checkGroup}>
            {presetsToShow.map((p) => (
              <label key={p.value} className={styles.checkItem}>
                <Checkbox
                  checked={presets.includes(p.value as PresetType)}
                  onCheckedChange={() => onChange('presets', toggle(presets, p.value as PresetType))}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
        )}
      </Field>

      {/* Audio-only format — only shown when audio_only preset selected */}
      {presets.includes('audio_only') && (
        <fieldset className={styles.fieldsetReset}>
          <legend className={styles.fieldLegend}>Audio-Only Format</legend>
          <RadioGroup
            value={audioOnlyFormat}
            onValueChange={(v) => onChange('audioOnlyFormat', v as 'aac' | 'mp3')}
            className={styles.radioGroup}
          >
            {(['aac', 'mp3'] as const).map((fmt) => (
              <label key={fmt} className={styles.radioItem}>
                <RadioGroupItem value={fmt} id={`audio-format-${fmt}`} />
                <span>{fmt.toUpperCase()}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      )}

      {/* Low Latency Mode — standalone checkbox row */}
      <label className={styles.checkItem}>
        <Checkbox
          checked={lowLatencyEnabled}
          onCheckedChange={(checked) => onChange('low_latency_enabled', checked === true)}
        />
        <span>Low Latency Mode</span>
      </label>
    </FormSectionCard>
  );
}
